import { Module } from "vuex";
import { $api } from "@/services/api";
import { SchoolConfig, LoanConfig } from "@/models/application";
import { InitialFundsRequest, InitialFundsResponse } from "@/models/opal";
import { RefiApplicationDetailsViewModel } from "@/models/refi";
import { PelLoanConfigurationDetails } from "@/models/pel";
import store from "@/store/index";
import {
  LoanConfigurationModule,
  RatePlanVM,
  RatePlanRepaymentVM,
  PelLoanOptions,
} from "@/models/loanConfiguration";
import { ProductIds } from "@/config/constants";
import useLoanConfigurationHelpers from "@/use/loanConfigurationModuleHelpers";
import { School, EnrollmentTerm } from "@/models/school";

const loanConfigurationModule: Module<LoanConfigurationModule, any> = {
  namespaced: true,
  state: {
    productTypeId: 0 as number,
    applicationInfo: {} as PelLoanConfigurationDetails,
    applicationInfoRefi: {} as RefiApplicationDetailsViewModel,
    initialDrawRequest: {} as InitialFundsRequest,
    schoolConfiguration: {} as SchoolConfig,
    loanConfiguration: {} as LoanConfig,
    selectedEnrollmentTerm: {} as EnrollmentTerm,
    selectedRateTerm: 0 as number,
    selectedRateType: "" as string,
    selectedRateTypeId: 0 as number,
    selectedRatePlanId: 0 as number,
    selectedRatePlan: {} as RatePlanVM,
    selectedRepaymentTypeId: 0 as number,
    selectedRepaymentOption: {} as RatePlanRepaymentVM,
    loanOptionsConfiguration: {} as PelLoanOptions,
    appRatePlans: [] as RatePlanVM[],
    fixedRatePlans: [] as RatePlanVM[],
    variableRatePlans: [] as RatePlanVM[],
    error: false as boolean,
    errorMessages: [] as string[],
    savingChanges: false as boolean,
    loading: false as boolean,
  },
  mutations: {
    SET_LOADING(state: LoanConfigurationModule, flag: boolean) {
      state.loading = flag;
    },
    SET_SAVING_CHANGES(state: LoanConfigurationModule, flag: boolean) {
      state.savingChanges = flag;
    },
    SET_PRODUCT_TYPE_ID(state: LoanConfigurationModule, productTypeId: number) {
      state.productTypeId = productTypeId;
    },
    SET_APPLICATION_INFO(state: LoanConfigurationModule, info: PelLoanConfigurationDetails) {
      state.applicationInfo = info;
    },
    SET_APPLICATION_INFO_REFI(
      state: LoanConfigurationModule,
      info: RefiApplicationDetailsViewModel,
    ) {
      state.applicationInfoRefi = info;
    },
    SET_APPLICATION_INFO_LOAN_AMOUNT(state: LoanConfigurationModule, loanAmount: number) {
      const info = state.applicationInfo as {
        loanAmount?: number;
        initialDraw?: { amount?: number };
      };
      if (info) {
        info.loanAmount = loanAmount;
        if (info.initialDraw) {
          info.initialDraw.amount = loanAmount;
        }
      }
    },
    SET_INITIAL_DRAW_REQUEST(
      state: LoanConfigurationModule,
      initialDrawRequest: InitialFundsRequest,
    ) {
      state.initialDrawRequest = initialDrawRequest;
    },
    SET_SCHOOL_CONFIGURATION(state: LoanConfigurationModule, schoolConfiguration: SchoolConfig) {
      state.schoolConfiguration = schoolConfiguration;
    },
    SET_LOAN_CONFIGURATION(state: LoanConfigurationModule, loanConfig: LoanConfig) {
      state.loanConfiguration = loanConfig;
    },
    SET_SELECTED_ENROLLMENT_TERM(state: LoanConfigurationModule, enrollmentTerm: EnrollmentTerm) {
      state.selectedEnrollmentTerm = enrollmentTerm;
    },
    SET_SELECTED_REPAYMENT_OPTION(
      state: LoanConfigurationModule,
      selectedRepaymentOption: RatePlanRepaymentVM,
    ) {
      state.selectedRepaymentOption = selectedRepaymentOption;
    },
    SET_SELECTED_RATE_TYPE_ID(state: LoanConfigurationModule, rateTypeId: number) {
      state.selectedRateTypeId = rateTypeId;
    },
    SET_SELECTED_RATE_PLAN(state: LoanConfigurationModule, selectedRatePlan: RatePlanVM) {
      state.selectedRatePlan = selectedRatePlan;
    },
    SET_SELECTED_REPAYMENT_TYPE_ID(state: LoanConfigurationModule, repaymentTypeId: number) {
      state.selectedRepaymentTypeId = repaymentTypeId;
    },
    SET_LOAN_OPTIONS_CONFIGURATION(state: LoanConfigurationModule, loanOptionsConfiguration: any) {
      state.loanOptionsConfiguration = loanOptionsConfiguration;
    },
    SET_APP_RATE_PLANS(state: LoanConfigurationModule, ratePlans: RatePlanVM[]) {
      state.appRatePlans = ratePlans;
    },
    SET_FIXED_RATE_PLANS(state: LoanConfigurationModule, ratePlans: RatePlanVM[]) {
      state.fixedRatePlans = ratePlans;
    },
    SET_VARIABLE_RATE_PLANS(state: LoanConfigurationModule, ratePlans: RatePlanVM[]) {
      state.variableRatePlans = ratePlans;
    },
    SET_ERROR(state: LoanConfigurationModule, error: boolean) {
      state.error = error;
    },
    SET_ERROR_MESSAGES(state: LoanConfigurationModule, errorMessages: string[]) {
      state.errorMessages = errorMessages;
    },
    SET_EDITABLE_FLAG(state: LoanConfigurationModule, flag: boolean) {
      state.applicationInfo.editable = flag;
    },
    SET_SELECTED_RATE_PLAN_ID(state: LoanConfigurationModule, selectedRatePlanId: number) {
      state.selectedRatePlanId = selectedRatePlanId;
    },
    SET_STUDENT_INFORMATION_MODAL(state: any, studentInformationModal: any) {
      state.studentInformationModal = studentInformationModal;
    },
  },
  actions: {
    setEditable({ commit }, flag: boolean) {
      commit("SET_EDITABLE_FLAG", flag);
    },
    setSavingChanges({ commit }, flag: boolean) {
      commit("SET_SAVING_CHANGES", flag);
    },
    setLoading({ commit }, flag: boolean) {
      commit("SET_LOADING", flag);
    },
    setInitialDrawRequest(
      { commit },
      initialDrawRequest: InitialFundsRequest | InitialFundsResponse,
    ) {
      commit("SET_INITIAL_DRAW_REQUEST", initialDrawRequest);
    },
    setSchoolConfiguration({ commit }, schoolConfiguration: SchoolConfig) {
      commit("SET_SCHOOL_CONFIGURATION", schoolConfiguration);
    },
    setLoanConfiguration({ commit }, loanConfiguration: LoanConfig) {
      commit("SET_LOAN_CONFIGURATION", loanConfiguration);
    },
    setSelectedEnrollmentTerm({ commit }, enrollmentTerm: EnrollmentTerm) {
      commit("SET_SELECTED_ENROLLMENT_TERM", enrollmentTerm);
    },
    setSelectedRatePlanId({ commit }, payload) {
      commit("SET_SELECTED_RATE_PLAN_ID", payload);
    },
    setSelectedRatePlan({ commit }, payload) {
      commit("SET_SELECTED_RATE_PLAN", payload);
    },
    setSelectedRepaymentTypeId({ commit }, repaymentTypeId: number) {
      commit("SET_SELECTED_REPAYMENT_TYPE_ID", repaymentTypeId);
    },
    setSelectedRepaymentOption({ commit }, repaymentOption: any) {
      commit("SET_SELECTED_REPAYMENT_OPTION", repaymentOption);
    },
    setSelectedRateTypeId({ commit }, rateTypeId: number) {
      commit("SET_SELECTED_RATE_TYPE_ID", rateTypeId);
    },
    async getPelDetails({ commit }, applicationId: number) {
      const response = await $api.pel.getPelDetails(applicationId);

      if (response.success) {
        commit("SET_APPLICATION_INFO", response.results);
        commit(
          "SET_PRODUCT_TYPE_ID",
          store.state.appData?.application?.productTypeId || ProductIds.PEL,
        );

        return true;
      } else {
        return false;
      }
    },
    async addOrUpdateInitialDraw(
      { state, dispatch },
      payload: {
        amount: number | string;
        editingFromConfirmYourLoanDetails?: boolean;
      },
    ) {
      const { amount, editingFromConfirmYourLoanDetails } = payload;

      if (store?.state?.appData?.application?.schoolConfiguration?.schoolId) {
        const schoolId = state.schoolConfiguration?.schoolId ?? 0;
        const enrollmentTerm = store.state.enrollmentTerms?.find(
          (p: EnrollmentTerm) => p.description === state.schoolConfiguration?.academicTerm,
        );

        const school = await $api.school.getSchool(schoolId);
        const index = store?.state?.appData?.applicants?.length - 1;

        const PELfallbackEnrollmentTermId =
          state.productTypeId === ProductIds.PEL
            ? (state.applicationInfo as PelLoanConfigurationDetails)?.academicTermId
            : null;

        const initialFundsRequest = {
          userId: store?.state?.appData?.applicants[index]?.userId,
          schoolDetailsId: school.schoolDetailsId,
          schoolDOEId: school.schoolDOEId,
          schoolBranchDOEId: school.schoolBranchDOEId,
          enrollmentTermId:
            enrollmentTerm?.id ||
            state.initialDrawRequest?.enrollmentTermId ||
            PELfallbackEnrollmentTermId,
          amount: amount,
        };

        const response = await $api.applications.addOrUpdateInitialDraw(
          store?.state?.appData.application.id,
          initialFundsRequest,
          editingFromConfirmYourLoanDetails,
        );

        if (response.applicationId) {
          await dispatch("getPelDetails", response.applicationId);
          dispatch("setInitialDrawRequest", response);
        }
        return response;
      } else {
        if (store?.state?.appData?.application?.schoolConfiguration?.schoolId === null) {
          return {
            errors: ["No School Selected"],
          };
        } else {
          return (state.applicationInfo as PelLoanConfigurationDetails)?.initialDraw;
        }
      }
    },
    async getAvailableRatePlans({ commit }) {
      commit("SET_LOADING", true);

      const applicationRatePlansResponse = await $api.pel.getRatePlans(
        store.state.appData.application?.id,
      );

      if (applicationRatePlansResponse.success) {
        commit("SET_LOAN_OPTIONS_CONFIGURATION", applicationRatePlansResponse.results);
        commit("SET_ERROR", false);
        commit("SET_ERROR_MESSAGES", []);
        commit(
          "SET_SELECTED_REPAYMENT_OPTION",
          applicationRatePlansResponse.results.selectedRepaymentOption,
        );

        commit("SET_SELECTED_RATE_PLAN", applicationRatePlansResponse.results.selectedRatePlan);

        if (applicationRatePlansResponse.results.appRatePlans?.length > 0) {
          commit("SET_APP_RATE_PLANS", applicationRatePlansResponse.results.appRatePlans);
        }

        if (applicationRatePlansResponse.results.fixedRatePlans?.length > 0) {
          commit("SET_FIXED_RATE_PLANS", applicationRatePlansResponse.results.fixedRatePlans || []);
        }

        if (applicationRatePlansResponse.results.variableRatePlans?.length > 0) {
          commit(
            "SET_VARIABLE_RATE_PLANS",
            applicationRatePlansResponse.results.variableRatePlans || [],
          );
        }
      } else {
        const errors = [
          "Something has gone wrong retrieving rates. If you continue to receive this error, please contact support.",
        ];
        commit("SET_ERROR", true);
        commit("SET_ERROR_MESSAGES", errors);
      }

      commit("SET_LOADING", false);
    },
    async updateLoanConfiguration(
      { state, commit, dispatch },
      editingFromConfirmYourLoanDetails?: boolean,
    ) {
      const { hasLoanConfigurationChanges } = useLoanConfigurationHelpers();

      commit("SET_ERROR", false);
      commit("SET_ERROR_MESSAGES", []);

      const request = {
        applicationId: store.state?.appData?.application?.id,
        selectedRatePlanId: state.selectedRatePlan.id,
        selectedByApplicantId: store.state.appData?.currentApplicant?.id,
        repaymentTypeId: state.selectedRepaymentOption?.repaymentTypeId,
      };

      const loanConfigurationChanged = hasLoanConfigurationChanges(
        (state.applicationInfo as PelLoanConfigurationDetails)?.loanConfiguration,
        request,
      );

      if (loanConfigurationChanged) {
        const response = await $api.applications.addOrUpdateAppLoanConfig(
          request,
          editingFromConfirmYourLoanDetails,
        );

        if (response && response.applicationId) {
          await dispatch("getPelDetails", response.applicationId);

          if (store.getters["checklistModule/rateAndRepaymentStipsExist"]) {
            await store.dispatch("checklistModule/getRateAndRepaymentStips");
          }

          store.dispatch("applicationInfoModule/closeModal", "PelLoanConfigurationModal");
          store.dispatch("loanConfigurationModule/setSavingChanges", false);
        } else {
          const errors = [
            "Something has gone wrong submitting your request. If you continue to receive this error, please contact support.",
          ];

          commit("SET_ERROR", true);
          commit("SET_ERROR_MESSAGES", errors);
          store.dispatch("loanConfigurationModule/setSavingChanges", false);
        }
      } else {
        store.dispatch("applicationInfoModule/closeModal", "PelLoanConfigurationModal");
        store.dispatch("loanConfigurationModule/setSavingChanges", false);
      }
    },
  },
  getters: {
    legalPacketGenerated: (state: LoanConfigurationModule): boolean => {
      return (state.applicationInfo as PelLoanConfigurationDetails)?.legalPacketGenerated || false;
    },
    legalPacketCreatedAtUtc: (state: LoanConfigurationModule): Date | null => {
      return (
        (state.applicationInfo as PelLoanConfigurationDetails)?.legalPacketCreatedAtUtc || null
      );
    },
    selectedSchool: (state: LoanConfigurationModule): School | null => {
      return (state.applicationInfo as PelLoanConfigurationDetails)?.selectedSchool || null;
    },
    appRatePlans: (state: LoanConfigurationModule): RatePlanVM[] => {
      if (state.selectedRateTypeId === 1) {
        return state.fixedRatePlans;
      } else if (state.selectedRateTypeId === 2) {
        return state.variableRatePlans;
      } else {
        return [];
      }
    },
  },
};

export default loanConfigurationModule;
